import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { createAppQueryClient } from '@siliconx/shared/query/queryClient';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { ErrorBoundary } from '@siliconx/shared/ui/ErrorBoundary';
import { routeTree } from './routeTree.gen';
import { API_BASE } from './lib/api';
import { client as authClient, getIdToken } from './lib/auth';
import './styles/globals.css';
import { initAnalytics, track } from '@siliconx/shared/analytics';

// SIL-1529: before the first render, so consent defaults precede any event.
const gaMeasurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
const hubspotPortalId = import.meta.env.VITE_HUBSPOT_PORTAL_ID;
// Skipped on /auth/callback: its URL carries the OAuth `?code=` (or
// `error_description`), which would reach GA4/HubSpot as page_location. The
// login flag that callback.tsx sets survives to the next page load.
if (gaMeasurementId && hubspotPortalId && window.location.pathname !== '/auth/callback') {
  initAnalytics({ gaMeasurementId, hubspotPortalId });
  // routes/auth/callback.tsx hard-redirects before gtag.js can load, so it
  // leaves this flag and the login event is sent on the next page load.
  try {
    if (sessionStorage.getItem('sx_analytics_login') === '1') {
      sessionStorage.removeItem('sx_analytics_login');
      track('login', {});
    }
  } catch {
    /* storage blocked - skip the login event */
  }
}

// SIL-667: 26,701 401s (37% of API traffic) came from three fixed-interval
// polling queries (notifications unread-count, opportunities badge,
// import-batches) that kept firing — and TanStack Query kept retrying —
// against an expired/absent token with no backoff and no give-up. This
// factory centralizes: never retry a 401, stop refetchInterval polling on
// a 401 (see authAwareRefetchInterval at each poll site), and route to
// re-auth via the shared AuthClient the first time any query hits one.
const queryClient = createAppQueryClient({
  retry: 1,
  onAuthExpired: () => authClient.silentRefreshOrRedirect(),
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('#root element not found');

createRoot(rootEl).render(
  <StrictMode>
    {/* SIL-666: root-of-tree crash catcher — renders a real fallback instead
        of a white screen, and reports to POST /api/client-error. Wraps
        RouterProvider itself (not just its Outlet) so a crash in router
        setup/context is caught too; route-level failures during
        load/render are separately caught by routes/__root.tsx's
        `errorComponent`. */}
    <ErrorBoundary portal="customer" apiBaseUrl={API_BASE} getToken={getIdToken}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
